import React, { useEffect, useState } from 'react';
import ThemedSelect from './ThemedSelect';
import AdminPagination from './AdminPagination';
import { PaginatedResponse } from '../services/api.types';
import {
  ServiceNotice,
  ServiceNoticeRecipient,
  ServiceNoticeStatus,
  fetchServiceNoticeRecipients,
} from '../services/serviceNotices';

const labels: Record<ServiceNoticeStatus, string> = {
  PENDING: 'În așteptare',
  SENDING: 'În curs',
  ACCEPTED: 'Acceptat de furnizor',
  FAILED: 'Respins',
  UNCERTAIN: 'Rezultat incert',
  SKIPPED: 'Omis',
};
const dateLabel = (value: string) => {
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value)
  );
};

interface AdminServiceNoticeRecipientsProps {
  notice: ServiceNotice;
  busy: boolean;
  onResolve: (recipientId: string, outcome: 'ACCEPTED' | 'NOT_SENT') => void;
}

const AdminServiceNoticeRecipients: React.FC<AdminServiceNoticeRecipientsProps> = ({
  notice,
  busy,
  onResolve,
}) => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ServiceNoticeStatus | ''>('');
  const [result, setResult] = useState<PaginatedResponse<ServiceNoticeRecipient> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    fetchServiceNoticeRecipients(notice.id, page, status)
      .then((data) => {
        if (!active) {
          return;
        }
        const lastPage = Math.max(1, data.totalPages);
        if (page > lastPage) {
          setPage(lastPage);
        } else {
          setResult(data);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [notice, page, status, refresh]);

  return (
    <div className="notice-recipients" aria-busy={loading}>
      <div className="notice-recipients-header">
        <div>
          <h3>Starea mesajelor</h3>
          <p className="notice-help">
            {result ? `${result.totalElements} destinatari în această listă` : 'Lista destinatarilor'}
          </p>
        </div>
        <ThemedSelect<ServiceNoticeStatus | ''>
          ariaLabel="Filtrează după starea mesajului"
          value={status}
          options={[
            { value: '', label: 'Toate stările' },
            ...Object.entries(labels).map(([value, label]) => ({
              value: value as ServiceNoticeStatus,
              label,
            })),
          ]}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
            setResult(null);
          }}
        />
      </div>
      <p className="notice-help">
        Pentru rezultate incerte, verifică istoricul furnizorului înainte de a confirma o rezolvare. Conturile
        șterse nu mai apar în listă.
      </p>
      {error ? (
        <div role="alert">
          Nu am putut încărca starea mesajelor.{' '}
          <button type="button" className="admin-legal-secondary" onClick={() => setRefresh((n) => n + 1)}>
            Încearcă din nou
          </button>
        </div>
      ) : !result ? (
        <p role="status">Se încarcă mesajele...</p>
      ) : (
        <>
          {result.content.length === 0 && <p role="status">Nu există mesaje cu această stare.</p>}
          {result.content.map((recipient) => (
            <div className="notice-recipient-row" key={recipient.id}>
              <div className="notice-recipient-identity">
                <strong>{recipient.name}</strong>
                <span>{recipient.email}</span>
              </div>
              <div className="notice-recipient-status">
                <span className={`notice-status notice-status-${recipient.status.toLowerCase()}`}>
                  {labels[recipient.status]}
                </span>
                {recipient.attemptedAt && <small>Încercare: {dateLabel(recipient.attemptedAt)}</small>}
                {recipient.acceptedAt && <small>Acceptare: {dateLabel(recipient.acceptedAt)}</small>}
                {recipient.resolvedAt && <small>Verificat manual: {dateLabel(recipient.resolvedAt)}</small>}
              </div>
              {recipient.status === 'UNCERTAIN' && (
                <div className="notice-actions">
                  <button
                    type="button"
                    className="admin-legal-secondary"
                    disabled={busy}
                    onClick={() => onResolve(recipient.id, 'ACCEPTED')}
                  >
                    Acceptat la furnizor
                  </button>
                  <button
                    type="button"
                    className="admin-legal-secondary"
                    disabled={busy}
                    onClick={() => onResolve(recipient.id, 'NOT_SENT')}
                  >
                    Confirmat netrimis
                  </button>
                </div>
              )}
            </div>
          ))}
          {result.totalPages > 1 && (
            <AdminPagination
              currentPage={page}
              totalPages={result.totalPages}
              onPageChange={(next) => {
                setPage(next);
                setResult(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
export default AdminServiceNoticeRecipients;
